"use client";

import { useEffect, useState } from "react";

/**
 * ====================
 * EASY CUSTOMIZATION
 * ====================
 *
 * To change the thinking words shown across the app:
 * 1. Edit the DEFAULT_THINKING_WORDS array below
 * 2. Add or remove words as desired
 * 3. The component will cycle through them in order
 *
 * To change the animation speed:
 * - Adjust the `interval` prop (default: 600ms)
 *
 * Example words you might use:
 * - Technical: 'Computing', 'Calculating', 'Generating'
 * - Casual: 'Hmm', 'Let me see', 'One moment'
 * - Playful: 'Cooking up ideas', 'Brewing thoughts', 'Conjuring magic'
 */

// Default thinking words for Cosimo search flow
const DEFAULT_THINKING_WORDS = [
  "Finding the best matches",
  "Curating products for you",
  "Matching with styles you'll love",
  "Finalizing your results",
];

interface ThinkingTextProps {
  /** Array of words to cycle through. Easy to customize! */
  words?: string[];
  /** Interval in milliseconds between word changes */
  interval?: number;
  /** Initial delay before showing first message (creates "reading" feel) */
  initialDelay?: number;
  /** Text color */
  color?: string;
  /** Text color for the active (latest) line */
  activeColor?: string;
  /** Text color for previous lines */
  inactiveColor?: string;
  /** Text style (fontSize, fontWeight, etc.) */
  style?: React.CSSProperties;
  /** Custom className */
  className?: string;
  /** Optional style for the ellipsis */
  ellipsisStyle?: React.CSSProperties;
  /** Callback when a new line appears */
  onLineChange?: (lineCount: number) => void;
}

/**
 * ThinkingText - Cycles through thinking words in order with smooth fade transitions
 *
 * Usage:
 * ```tsx
 * <ThinkingText />
 * <ThinkingText words={['Custom', 'Words', 'Here']} />
 * <ThinkingText interval={1500} color="#000" />
 * ```
 */
export function ThinkingText({
  words = DEFAULT_THINKING_WORDS,
  interval = 3500, // Slower default: 3.5 seconds between changes
  initialDelay = 900, // Delay before first message appears
  color,
  activeColor,
  inactiveColor,
  style = {},
  className = "",
  ellipsisStyle,
  onLineChange,
}: ThinkingTextProps) {
  const [visibleLineCount, setVisibleLineCount] = useState(0);

  // Show first line after initial delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setVisibleLineCount(1);
    }, initialDelay);
    return () => clearTimeout(timer);
  }, [words, initialDelay]);

  const visibleWords = words.slice(0, Math.max(0, visibleLineCount));

  // Notify parent when line count changes
  useEffect(() => {
    onLineChange?.(visibleLineCount);
  }, [visibleLineCount, onLineChange]);

  // Add subsequent lines at regular interval
  useEffect(() => {
    if (words.length === 0) return;
    if (visibleLineCount === 0) return; // Wait for initial delay
    if (visibleLineCount >= words.length) return;

    const timer = setInterval(() => {
      setVisibleLineCount((prev) => Math.min(prev + 1, words.length));
    }, interval);

    return () => clearInterval(timer);
  }, [words, interval, visibleLineCount]);

  const Ellipsis = (
    <span className="ml-3 inline-flex" aria-hidden="true">
      <span
        className="inline-block animate-bounce"
        style={{ animationDelay: "0ms", ...ellipsisStyle }}
      >
        .
      </span>
      <span
        className="inline-block animate-bounce"
        style={{ animationDelay: "150ms", ...ellipsisStyle }}
      >
        .
      </span>
      <span
        className="inline-block animate-bounce"
        style={{ animationDelay: "300ms", ...ellipsisStyle }}
      >
        .
      </span>
    </span>
  );

  return (
    <div className="flex flex-col gap-1.5" data-testid="thinking-text">
      {visibleWords.map((word, index) => (
        <div key={`${word}-${index}`} className="animate-in fade-in duration-300 ease-out">
          <span
            className={className}
            style={{
              color:
                index === visibleWords.length - 1
                  ? (activeColor ?? color)
                  : (inactiveColor ?? color),
              ...style,
            }}
          >
            <>
              {word}
              {index === visibleWords.length - 1 ? Ellipsis : null}
            </>
          </span>
        </div>
      ))}
    </div>
  );
}
